import React, { useState } from "react";
import {
  IconX,
  IconTruck,
  IconMapPin,
  IconClipboardText,
  IconPackage,
  IconInfoCircle,
  IconLoader2,
  IconHistory,
  IconPhoto,
  IconCheck,
  IconCloudDown,
  IconUser,
  IconPhone,
  IconMail,
  IconUserCheck,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useFetchReturnToShipperDetails } from "../../../apis/returnToShipperApi";
import { productManifestApi } from "../../../apis/productManifestApi";
import formatDate, { formatDateTime } from "../../../utilities/helper";
import ButtonStyled from "../../../components/common/ButtonStyled";

type Props = {
  manifest: any;
  onClose: () => void;
  onUpdateProductManifest?: () => void;
  onVerifyIfHold?: () => void;
  onAssignClick?: () => void;
};

const Row = ({
  label,
  value,
  className,
}: {
  icon?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => (
  <div className={`flex items-center justify-between gap-4 py-2.5 px-6 text-sm ${className || ""}`}>
    <span className="font-medium text-[#212121] shrink-0">{label}:</span>
    <span className="text-[#212121] text-right font-normal break-all">{value || ""}</span>
  </div>
);

const SectionHeader = ({ title }: { icon?: any; title: string }) => (
  <div className="mb-2">
    <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">
      {title}
    </p>
  </div>
);

const formatQuantity = (value: number | undefined | null): string => {
  return (value === 0 || value === undefined || value === null) ? "" : String(value);
};

const ManifestDetailsSidebar = ({ manifest, onClose, onUpdateProductManifest, onVerifyIfHold, onAssignClick }: Props) => {
  const [activeTab, setActiveTab] = useState<"OVERVIEW" | "CARGO ITEMS" | "ACTIVITY LOG" | "POD MEDIA">("OVERVIEW");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const id = manifest?.id || manifest?.manifestId || "";
  const type = manifest?.type || "pickup";

  // 1. Pickup Detail Query
  const { data: pickupResponse, isFetching: isPickupFetching } = useQuery({
    queryKey: ["pickupDetails", id],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL || ""}/api/client/pickup-request/shipments/${id}?page=1&limit=100&type=lmClient`,
        { credentials: "include" }
      );
      return res.json();
    },
    enabled: type === "pickup" && !!id,
  });

  const pickupData = pickupResponse?.data || {};
  const pickupDetails = pickupData?.pickupRequest || {};

  // 4. Pickup Detail By ID Query (to fetch statusHistories and metadata)
  const { data: pickupDetailsByIdResponse, isFetching: isPickupDetailsByIdFetching } = useQuery({
    queryKey: ["pickupDetailsById", id],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL || ""}/api/client/pickup-request/shipmentsById/${id}?type=lmClient`,
        { credentials: "include" }
      );
      return res.json();
    },
    enabled: type === "pickup" && !!id,
  });

  const pickupDetailsById = pickupDetailsByIdResponse?.data || {};
  const pickupShipments = pickupDetailsById?.nonRevertedShipments || pickupDetailsById?.generatedShipments || pickupData?.generatedShipments || [];

  // 2. Return Detail Query
  const { data: returnData, isFetching: isReturnFetching } = useFetchReturnToShipperDetails(1, 100, "", type === "return" ? id : "");
  const returnDetails = returnData || {};
  const returnShipments = returnData?.shipments || [];

  // 3. Product Detail Query
  const { data: productResponse, isFetching: isProductFetching } = useQuery({
    queryKey: ["productDetails", id],
    queryFn: async () => {
      const res = await productManifestApi.getManifestById(id);
      return res?.data;
    },
    enabled: type === "product" && !!id,
  });
  
  const productDetails = productResponse || {};

  // Resolve details object for simple field mapping
  const details = {
    ...manifest,
    ...pickupDetails,
    ...pickupData,
    ...pickupDetailsById,
    ...returnDetails,
    ...returnData,
    ...productDetails,
  };

  // Resolve status histories based on manifest type
  let statusHistories: any[] = [];
  if (type === "pickup") {
    statusHistories = pickupDetailsById?.statusHistories || [];
  } else if (type === "product") {
    statusHistories = productResponse?.statusHistories || [];
  } else if (type === "return") {
    statusHistories = returnData?.statusHistories || [];
  } else {
    statusHistories = manifest?.statusHistories || [];
  }

  // Resolve signature and cargo image based on manifest type
  let signature = "";
  let image = "";
  if (type === "pickup") {
    signature = pickupDetailsById?.signature || manifest?.signature || "";
    image = pickupDetailsById?.image || pickupDetailsById?.pickupImage || manifest?.image || manifest?.pickupImage || "";
  } else if (type === "product") {
    signature = productResponse?.signature || manifest?.signature || "";
    image = productResponse?.image || manifest?.image || "";
  } else if (type === "return") {
    signature = returnData?.signature || manifest?.signature || "";
    image = returnData?.image || manifest?.image || "";
  } else {
    signature = manifest?.signature || "";
    image = manifest?.image || manifest?.pickupImage || "";
  }

  const isFetching = isPickupFetching || isReturnFetching || isProductFetching || isPickupDetailsByIdFetching;

  const STATUS_COLORS: Record<string, string> = {
    GENERATED: "bg-amber-50 border border-amber-300 text-amber-800",
    ASSIGNED: "bg-blue-50 border border-blue-300 text-blue-800",
    ASSIGNED_TO_DRIVER: "bg-blue-50 border border-blue-300 text-blue-800",
    ASSIGNED_TO_STAFF: "bg-indigo-50 border border-indigo-300 text-indigo-800",
    COLLECTED: "bg-slate-50 border border-slate-300 text-slate-700",
    COMPLETED: "bg-green-50 border border-green-300 text-green-800",
    PENDING: "bg-red-50 border border-red-300 text-red-800",
    VERIFIED: "bg-green-50 border border-green-300 text-green-800",
  };

  const fmtStatus = (s: string) =>
    s ? s.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

  const handleDownload = (docUrl?: string) => {
    if (!docUrl) return;
    window.open(docUrl, "_blank");
  };

  const renderMediaPreview = (url: string) => {
    const isPdf = url.toLowerCase().includes(".pdf") || url.toLowerCase().includes("pdf");
    
    if (isPdf) {
      return (
        <div className="flex flex-col items-center gap-3 w-full p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <div className="p-3 bg-red-50 text-red-500 rounded-full">
            <IconClipboardText size={32} />
          </div>
          <p className="text-xs font-semibold text-slate-700">PDF Verification Document</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDownload(url)}
              className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
            >
              Open / Download PDF
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-3 w-full p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
        <img
          src={url}
          alt="Verification Media"
          className="max-h-40 object-contain rounded-lg border border-slate-200 bg-white p-1"
        />
        <button
          onClick={() => handleDownload(url)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
        >
          <IconCloudDown size={16} /> Open / Download Image
        </button>
      </div>
    );
  };

  return (
    <div className="relative border rounded-lg border-gray-200 flex flex-col overflow-hidden bg-transparent w-full h-full lg:h-[calc(100vh-170px)]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-transparent flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-[#212121] truncate">
            {manifest?.manifestId || id}
          </h2>
          <div className="flex items-center flex-wrap gap-2 mt-1.5">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">
              Type : {type === "pickup" ? "Pickup" : type === "return" ? "Return" : "Product"}
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">
              Date : {formatDate(details.createdAt || details.date || details.pickupDate)}
            </span>
            {details.timeSlot && (
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">
                Time : {details.timeSlot}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center rounded-full py-1 px-3 text-xs font-medium bg-[#F4F4F4] text-[#212121] whitespace-nowrap select-none border border-transparent">
            {(details.status || "").replace(/_/g, " ")}
          </span>

          {type === "product" && (
            <>
              {(details.status === "MANIFEST_REQUEST" || details.status === "ON_PROCESS") && (
                <ButtonStyled
                  onClick={onUpdateProductManifest}
                  variant="outline"
                  value="Update Product Manifest"
                />
              )}
              {details.status === "ON_HOLD" && (
                <ButtonStyled
                  onClick={onVerifyIfHold}
                  variant="outline"
                  value="Verify if Hold"
                />
              )}
            </>
          )}

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0 bg-transparent">
        {isFetching ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2 text-slate-400">
            <IconLoader2 className="animate-spin" size={24} />
            <span className="text-xs font-semibold">Loading details...</span>
          </div>
        ) : (
          <div className="px-6 pt-1 pb-2 space-y-2">
            {/* Tabs */}
            {(() => {
              let cargoCount = 0;
              if (type === "pickup") {
                cargoCount = pickupShipments.length || pickupDetails?.qty || manifest?.qty || 0;
              } else if (type === "return") {
                cargoCount = returnShipments.length || manifest?.qty || 0;
              } else if (type === "product") {
                cargoCount = productDetails?.product?.length || productDetails?.products?.length || manifest?.qty || 0;
              }

              return (
                <div className="-mx-6 border-b border-gray-200 px-3 py-1.5 flex items-center gap-1.5 overflow-x-auto custom-scrollbar max-w-[calc(100%+3rem)] mb-3">
                  {[
                    { key: "OVERVIEW", label: "Overview" },
                    { key: "CARGO ITEMS", label: type === "product" ? "SKU" : "AWB", badge: cargoCount },
                    { key: "ACTIVITY LOG", label: "Timeline" },
                    { key: "POD MEDIA", label: "POD Media" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key as any)}
                      className={`py-1.5 px-2.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center border shrink-0 flex-1 whitespace-nowrap gap-1.5 ${
                        activeTab === tab.key
                          ? "bg-[#FBD037]/20 text-[#000000] shadow-sm border-[#FBD037]"
                          : "bg-slate-100 text-slate-600 border-slate-200"
                      }`}
                    >
                      {tab.label}
                      {tab.badge !== undefined && tab.badge > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full font-semibold">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              );
            })()}

            <>
              {activeTab === "OVERVIEW" && (
                <div className="-mx-6 divide-y divide-gray-200/60 pt-1">
                  <Row label="Client Name" value={details.clientName || details.client} />
                  <Row label="Phone" value={details.mobileNo || details.clientMobile || details.clientMobileNo} />
                  <Row label="Email" value={details.email || details.clientEmail} />
                  <Row label="Address" value={details.location || details.fullAddress || details.locationName || (details.pickupLocation ? details.pickupLocation.full_address : "")} />
                  {(details.driverName || details.assignedResource) && (
                    <Row label="Driver Name" value={details.driverName || details.assignedResource} />
                  )}
                  {details.driverCode && (
                    <Row label="Driver Code" value={details.driverCode} />
                  )}
                  {(details.driverMobileNo || details.driverMobile) && (
                    <Row label="Contact" value={details.driverMobileNo || details.driverMobile} />
                  )}
                  {details.vehicleType && (
                    <Row label="Vehicle" value={details.vehicleType} />
                  )}
                  {details.createdByName && (
                    <Row label="Assigned By" value={details.createdByName} />
                  )}
                  {details.createdAt && (
                    <Row label="Assigned At" value={formatDateTime(details.createdAt)} />
                  )}

                  {/* Uploaded Document Card */}
                  {image && type === "return" && (
                    <div className="pt-3">
                      <div className="relative">
                        {renderMediaPreview(image)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "CARGO ITEMS" && (
                <div className="space-y-4 font-sans text-xs">
                  {type !== "product" ? (
                    <div className="space-y-3">
                      {/* FILTER ROW MATCHING LAST MILE */}
                      <div className="flex items-center justify-end gap-1.5 py-1">
                        <span className="font-semibold text-slate-500 text-[10px] uppercase tracking-wider">
                          FILTER:
                        </span>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="bg-white border border-slate-200 text-[#212121] rounded-lg px-2 py-0.5 text-xs font-semibold focus:outline-none focus:border-[#FBD037]/80 cursor-pointer shadow-sm"
                        >
                          <option value="ALL">All Statuses</option>
                          <option value="PICKUP_GENERATED">Pickup Generated</option>
                          <option value="READY_FOR_PICKUP">Ready for Pickup</option>
                          <option value="PICKUP_COLLECTED">Pickup Collected</option>
                          <option value="ASSIGNED_TO_STAFF">Assigned To Staff</option>
                          <option value="RECEIVE_IN_WAREHOUSE">Receive in Warehouse</option>
                          <option value="REVERTED">Reverted / New Order</option>
                        </select>
                      </div>

                      {(() => {
                        const rawShipments = type === "pickup" ? pickupShipments : returnShipments;
                        const filteredShipments = (rawShipments || []).filter((s: any) => {
                          if (statusFilter === "ALL") return true;
                          const st = (s.status || s.to || "").toUpperCase();
                          return st === statusFilter;
                        });

                        const getStatusClasses = (status: string) => {
                          const lower = (status || "").toLowerCase();
                          if (lower === "reverted") {
                            return "bg-rose-50 text-rose-700 border-rose-200";
                          }
                          if (lower.includes("delivery") || lower.includes("dispatch") || lower.includes("loaded")) {
                            return "bg-green-50 text-green-700 border-green-200";
                          } else if (lower.includes("delivered") || lower.includes("completed")) {
                            return "bg-gray-50 text-gray-700 border-gray-200";
                          } else if (lower.includes("undelivered")) {
                            return "bg-amber-50 text-amber-700 border-amber-200";
                          }
                          return "bg-blue-50 text-blue-700 border-blue-200";
                        };

                        return filteredShipments.length > 0 ? (
                          <div className="divide-y divide-gray-200/60">
                            {filteredShipments.map((awb: any, index: number) => {
                              const formattedStatus = (awb.status || "")
                                .toLowerCase()
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (char: string) => char.toUpperCase());

                              return (
                                <div
                                  key={awb.id || index}
                                  className="py-3 px-1 transition-all duration-300 text-xs flex justify-between items-center"
                                >
                                  <span
                                    onClick={() => window.open(`/orders/${awb.awbNumber || awb.awb}`, "_blank")}
                                    className="font-bold text-sm text-blue-600 hover:underline cursor-pointer"
                                  >
                                    {awb.awbNumber || awb.awb || ""}
                                  </span>
                                  <span
                                    className={`rounded-full px-2.5 py-0.5 border text-[10px] font-semibold ${getStatusClasses(awb.status)}`}
                                  >
                                    {formattedStatus || "Loaded"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="w-full p-6 border border-slate-200/70 rounded-2xl bg-white shadow-sm text-center text-xs text-slate-400">
                            No Associated Waybills
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="w-full space-y-3">
                      <SectionHeader icon={IconPackage} title={`Product Info (${details?.products?.length || details?.items?.length || 0})`} />
                      <div className="w-full mt-2">
                        {(() => {
                          const rawProducts = details?.products || details?.items || [];
                          if (rawProducts.length === 0) {
                            return (
                              <div className="py-8 text-center text-xs text-slate-400">
                                No products found
                              </div>
                            );
                          }

                          const mergedMap = new Map<string, any>();
                          rawProducts.forEach((item: any) => {
                            const sku = item?.sku || item?.productItem?.sku || "";
                            if (!sku) return;
                            if (mergedMap.has(sku)) {
                              const existing = mergedMap.get(sku);
                              mergedMap.set(sku, {
                                ...existing,
                                quantity: (existing.quantity || 0) + (item.quantity || item.qty || 0),
                                missingQty: (existing.missingQty || 0) + (item.missingQty || 0),
                                damageQty: (existing.damageQty || 0) + (item.damageQty || 0),
                                receivedQty: (existing.receivedQty || 0) + (item.receivedQty || 0),
                                reservedQuantity: (existing.reservedQuantity || 0) + (item.reservedQuantity || 0),
                                note: existing.note || item.note,
                              });
                            } else {
                              mergedMap.set(sku, {
                                ...item,
                                sku,
                                quantity: item.quantity || item.qty || 0,
                                hasExpiry: item.hasExpiry || item.productItem?.hasExpiry,
                                itemName: item.itemName || item.productItem?.itemName || ""
                              });
                            }
                          });

                          const mergedProducts = Array.from(mergedMap.values());

                          return (
                            <div className="grid grid-cols-1 gap-4 mt-2">
                              {mergedProducts.map((item: any, i: number) => (
                                <div
                                  key={item.sku || i}
                                  className="py-2 border-b border-gray-100 space-y-1"
                                >
                                  <div className="relative flex items-center justify-between gap-2">
                                    <div className="min-w-0 max-w-[200px]">
                                      <p className="text-[11px] uppercase tracking-wider text-[#212121]/50 font-semibold">
                                        SKU
                                      </p>
                                      <h3
                                        className="text-sm font-bold text-[#212121] truncate"
                                        title={item?.sku || ""}
                                      >
                                        {item?.sku
                                          ? item.sku.length > 18
                                            ? `${item.sku.slice(0, 16)}...`
                                            : item.sku
                                          : ""}
                                      </h3>
                                    </div>

                                    <div className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-700 shrink-0">
                                      Qty : {formatQuantity(item?.quantity)}
                                    </div>
                                  </div>

                                  <div className="relative">
                                    <Row label="Received Qty" value={formatQuantity(item?.receivedQty)} />
                                    <Row
                                      label="Expiry Date"
                                      value={
                                        item?.expiryDate
                                          ? new Date(item.expiryDate).toISOString().split("T")[0]
                                          : ""
                                      }
                                    />
                                    <Row label="Missing Qty" value={formatQuantity(item?.missingQty)} />
                                    <Row label="Damage Qty" value={formatQuantity(item?.damageQty)} />
                                    <Row label="Admin Reason" value={item?.adminReason || ""} />
                                    <Row label="Client Reason" value={item?.clientReason || ""} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "ACTIVITY LOG" && (
                <div className="space-y-3">
                  <div className="relative">
                    <SectionHeader icon={IconHistory} title="Audit Trail History Timestamps" />
                    {statusHistories.length > 0 ? (
                      <div className="space-y-0 relative pl-4 border-l border-emerald-200/60 ml-2">
                        {statusHistories.map((item: any, index: number) => {
                          const isLast = index === statusHistories.length - 1;
                          return (
                            <div key={item.id || index} className="relative pb-6 last:pb-0">
                              {/* Dot Indicator */}
                              {isLast ? (
                                <div className="absolute -left-[24px] top-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md border border-white ring-2 ring-emerald-600/30">
                                  <IconCheck size={10} stroke={4} />
                                </div>
                              ) : (
                                <div className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-2 ring-emerald-500/20 shadow-xs" />
                              )}
                              <div>
                                <p className="text-xs font-semibold text-slate-800">
                                  {(item.newStatus || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                </p>
                                {item.details && (
                                  <p className="text-[11px] text-slate-500 mt-0.5">{item.details}</p>
                                )}
                                <p className="text-[10px] text-slate-400 mt-1">
                                  {formatDateTime(item.createdAt)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-xs text-slate-400">
                        No activity history logs found.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "POD MEDIA" && (
                <div className="space-y-6">
                  {/* Proof of Return signature & photo */}
                  <div className="space-y-3">
                    <div className="relative">
                      <SectionHeader icon={IconClipboardText} title="Proof of Return Signature" />
                      <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        {signature ? (
                          <div className="flex flex-col items-center gap-3">
                            <img
                              src={signature}
                              alt="Signature"
                              className="max-h-24 object-contain rounded-lg border border-slate-200 bg-white p-1"
                            />
                            <button
                              onClick={() => window.open(signature, "_blank")}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                            >
                              <IconCloudDown size={16} /> Open / Download Signature
                            </button>
                          </div>
                        ) : (
                          <div className="text-center py-2 text-slate-400 flex flex-col items-center gap-2">
                            <IconClipboardText size={32} className="opacity-30 text-[#212121]" />
                            <p className="text-xs italic">Digital Signature Signed Off</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">Signature details available on complete report download</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="relative">
                      <SectionHeader icon={IconPhoto} title="Cargo Photo Verification (Proof of Pickup)" />
                      <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        {image ? (
                          renderMediaPreview(image)
                        ) : (
                          <div className="text-center py-4 text-slate-400 flex flex-col items-center gap-2">
                            <IconPhoto size={32} className="opacity-30 text-[#212121]" />
                            <p className="text-xs italic">No verification photo media available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManifestDetailsSidebar;
